import { queryOptions, useQuery } from '@tanstack/react-query';
import { ActivityService, VolumeEstimationDTO } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { AttributeChipData } from '../../../components/Prediction/types';
import { STALE_TIME_LIVE_VIEW } from '../../queryClient';
import { transformVolumeEstimation, VolumeEstimationConfig } from '../volumeEstimation';

const defaultVolumeEstimationConfig: VolumeEstimationConfig = { decimalPlaces: 2 };

export const activityVolumeEstimationnKeys = {
  base: () => ['activityVolumeEstimation'] as const,
  one: ({ jobsiteId, captureStartAt, config }: ActivityVolumeEstimationOptionProps) =>
    [...activityVolumeEstimationnKeys.base(), jobsiteId, captureStartAt, config] as const,
};

export interface ActivityVolumeEstimationOptionProps {
  jobsiteId: string;
  captureStartAt?: string;
  config?: VolumeEstimationConfig;
}

export function activityVolumeEstimationOptions({
  jobsiteId,
  captureStartAt,
  config = defaultVolumeEstimationConfig,
  enabled,
}: ActivityVolumeEstimationOptionProps & { enabled: boolean }) {
  const transformFn = (resp: VolumeEstimationDTO) => transformVolumeEstimation(resp, config);

  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: activityVolumeEstimationnKeys.one({ jobsiteId, captureStartAt, config }),
    queryFn: async () => {
      if (!jobsiteId || !captureStartAt) {
        throw new Error('jobsiteId and captureStartAt are required');
      }

      const response = await ActivityService.activityControllerGetVolumeEstimation({
        path: { jobsiteId, capture_start_at: captureStartAt },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!captureStartAt && enabled,
    staleTime: STALE_TIME_LIVE_VIEW,
  });
}

export function useActivityVolumeEstimation(props: ActivityVolumeEstimationOptionProps & { enabled: boolean }) {
  return useQuery(activityVolumeEstimationOptions(props));
}
